import React from "react";
import { motion } from "framer-motion";
import { Crown, ShieldCheck, ExternalLink, MapPin, MessageCircle, Medal, Lightbulb, Zap, Phone } from "lucide-react";

// Rank 1-3 podium accents (gold / silver / bronze). Rank 4+ falls back to clean glass.
const PODIUM = {
  1: {
    wrap: "border-amber-400/50 bg-gradient-to-r from-amber-500/[0.08] to-transparent shadow-[0_0_40px_-8px_rgba(251,191,36,0.45)]",
    accent: "text-amber-300", num: "text-amber-300", bid: "text-amber-300",
    icon: <Crown className="w-5 h-5 sm:w-7 sm:h-7 text-amber-300 drop-shadow-[0_0_8px_rgba(251,191,36,0.7)]" />,
    label: "Gold — Top spot",
  },
  2: {
    wrap: "border-slate-300/40 bg-gradient-to-r from-slate-300/[0.06] to-transparent",
    accent: "text-slate-200", num: "text-slate-200", bid: "text-slate-100",
    icon: <Medal className="w-5 h-5 sm:w-6 sm:h-6 text-slate-200" />,
    label: "Silver",
  },
  3: {
    wrap: "border-orange-500/40 bg-gradient-to-r from-orange-600/[0.07] to-transparent",
    accent: "text-orange-300", num: "text-orange-300", bid: "text-orange-200",
    icon: <Medal className="w-5 h-5 sm:w-6 sm:h-6 text-orange-300" />,
    label: "Bronze",
  },
};

function Avatar({ src, name, size }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  return (
    <div className={`${size} rounded-xl overflow-hidden glass-strong flex items-center justify-center shrink-0 font-display font-bold`}>
      {src ? <img src={src} alt={name} className="w-full h-full object-cover" /> : initial}
    </div>
  );
}

function WhatsAppButton({ number }) {
  const digits = String(number).replace(/[^0-9]/g, "");
  if (!digits) return null;
  return (
    <a
      data-testid="whatsapp-btn"
      href={`https://wa.me/${digits}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-black font-semibold text-xs sm:text-sm px-3 py-1.5 active:scale-95 transition-[transform,background-color] shrink-0"
    >
      <MessageCircle className="w-4 h-4" /> Chat
    </a>
  );
}

export function BidCard({ entry, currency = "$", onNudge, showPhone = false }) {
  const rank = entry.rank;
  const inactive = !entry.is_active;
  const podium = rank <= 3 && !inactive;
  const cfg = podium ? PODIUM[rank] : null;
  const isGold = podium && rank === 1;

  const showRank1Nudge = rank === 2 && entry.amount_to_rank_1 != null;
  const showPodiumNudge = rank >= 4 && entry.amount_to_podium != null;
  const clickNudge = (e) => {
    e.stopPropagation();
    if (onNudge) onNudge(entry.nudge_target_cap);
  };

  return (
    <motion.div
      layout="position"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
      data-testid={`rank-${rank}-row`}
      className={`rounded-2xl sm:rounded-3xl border transition-colors flex items-center gap-3 sm:gap-5 ${
        podium
          ? `${cfg.wrap} ${isGold ? "p-5 sm:p-7" : "p-4 sm:p-6"}`
          : `glass border-white/10 p-4 sm:p-5 hover:border-white/20 ${inactive ? "opacity-70" : ""}`
      }`}
    >
      {/* Rank indicator — always visible */}
      <div className="flex flex-col items-center shrink-0 w-8 sm:w-12">
        {podium && cfg.icon}
        <span className={`font-display font-black leading-none ${podium ? `${cfg.num} ${isGold ? "text-2xl sm:text-5xl" : "text-xl sm:text-3xl"}` : "text-lg sm:text-2xl text-white/40"}`}>
          {rank}
        </span>
      </div>

      <Avatar src={entry.logo_url} name={entry.business_name} size={isGold ? "w-12 h-12 sm:w-20 sm:h-20" : "w-11 h-11 sm:w-14 sm:h-14"} />

      <div className="min-w-0 flex-1">
        {podium && (
          <div className={`text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] mb-0.5 ${cfg.accent}`}>{cfg.label}</div>
        )}
        <div className="flex items-start gap-2">
          <h3 data-testid={`rank-${rank}-name`} className={`font-display font-bold tracking-tight leading-tight break-normal line-clamp-2 ${isGold ? "text-lg sm:text-2xl" : "text-base sm:text-lg"}`}>
            {entry.business_name}
          </h3>
          {entry.is_brand_lock && <ShieldCheck data-testid="brand-lock-badge" className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 shrink-0 mt-0.5" />}
        </div>

        {(entry.location_text || entry.short_description) && (
          <div className="mt-1 space-y-0.5">
            {entry.location_text && (
              <div className="flex items-center gap-1 text-xs text-white/45">
                <MapPin className="w-3 h-3 shrink-0" /> <span className="truncate">{entry.location_text}</span>
              </div>
            )}
            {entry.short_description && (
              <p className="text-xs sm:text-sm text-white/55 line-clamp-1">{entry.short_description}</p>
            )}
          </div>
        )}

        <div className="flex items-center gap-3 mt-1.5">
          {entry.website && (
            <a
              href={entry.website.startsWith("http") ? entry.website : `https://${entry.website}`}
              target="_blank" rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1 text-xs text-white/40 hover:text-white/80 transition-colors truncate"
            >
              <span className="truncate">{entry.website.replace(/^https?:\/\//, "")}</span> <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
          )}
          {entry.whatsapp_number && <WhatsAppButton number={entry.whatsapp_number} />}
          {entry.google_maps_url && (
            <a
              data-testid="maps-link"
              href={entry.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title="Open in Google Maps"
              className="inline-flex items-center gap-1 text-xs text-white/40 hover:text-blue-300 transition-colors shrink-0"
            >
              <MapPin className="w-4 h-4" /> Map
            </a>
          )}
          {showPhone && entry.phone_number && (
            <a
              data-testid="phone-link"
              href={`tel:${String(entry.phone_number).replace(/[^0-9+]/g, "")}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-xs text-white/40 hover:text-emerald-300 transition-colors shrink-0"
            >
              <Phone className="w-4 h-4" /> {entry.phone_number}
            </a>
          )}
        </div>

        {showRank1Nudge && (
          onNudge ? (
            <button
              data-testid={`nudge-rank1-${rank}`}
              onClick={clickNudge}
              className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-200 hover:bg-amber-500/25 text-[11px] sm:text-xs font-semibold px-3 py-1.5 active:scale-95 transition-[transform,background-color] text-left"
            >
              <Lightbulb className="w-3.5 h-3.5 shrink-0" />
              Bid +{currency}{Number(entry.amount_to_rank_1).toFixed(2)} to snatch #1{entry.rank_1_name ? ` from ${entry.rank_1_name}` : ""}
            </button>
          ) : (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 border border-amber-400/30 text-amber-200/80 text-[11px] sm:text-xs font-semibold px-3 py-1.5">
              <Lightbulb className="w-3.5 h-3.5 shrink-0" />
              +{currency}{Number(entry.amount_to_rank_1).toFixed(2)} to reach #1
            </div>
          )
        )}

      </div>

      {/* Bid amount (active) or Free-tier badge (inactive/paused) */}
      <div className="text-right shrink-0 leading-none">
        {inactive ? (
          <span data-testid={`rank-${rank}-freetier`} className="inline-block rounded-full bg-white/5 border border-white/10 text-white/40 text-[10px] sm:text-xs font-semibold px-2.5 py-1">
            Inactive · Free Tier
          </span>
        ) : (
          <div data-testid={`rank-${rank}-bid`}>
            <div className={`font-display font-black tabular-nums whitespace-nowrap ${podium ? cfg.bid : "text-white"} ${isGold ? "text-2xl sm:text-4xl" : "text-lg sm:text-2xl"}`}>
              {currency}{Number(entry.locked_bid_amount ?? entry.max_daily_cap).toFixed(2)}
            </div>
            <div className="text-[10px] sm:text-xs text-white/40 font-sans mt-0.5">locked</div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
